/**
 * Merina HMS - DOM Enhancer
 * ==========================
 * Runtime accessibility improvements using MutationObserver.
 * Automatically adds ARIA labels, roles, and keyboard navigation.
 * ZERO changes to existing components.
 */

class DOMEnhancer {
  constructor() {
    this.observer = null;
    this.processedElements = new WeakSet();
    this.initialized = false;
  }

  /**
   * Initialize the DOM enhancer
   */
  init() {
    if (this.initialized) return;

    // Initial enhancement pass
    this.enhanceDOM(document.body);

    // Setup MutationObserver for dynamic content
    this.setupObserver();

    this.initialized = true;
    console.log('[Accessibility] DOM Enhancer initialized');
  }

  /**
   * Setup MutationObserver to watch for DOM changes
   */
  setupObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.enhanceDOM(node);
          }
        });
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Enhance a DOM subtree
   */
  enhanceDOM(root) {
    if (!root || this.processedElements.has(root)) return;

    // Enhance buttons
    this.enhanceButtons(root);

    // Enhance inputs
    this.enhanceInputs(root);

    // Enhance links
    this.enhanceLinks(root);

    // Enhance images
    this.enhanceImages(root);

    // Enhance interactive elements
    this.enhanceInteractiveElements(root);

    // Enhance navigation
    this.enhanceNavigation(root);

    // Enhance tables
    this.enhanceTables(root);

    // Mark as processed
    this.processedElements.add(root);
  }

  /**
   * Enhance button elements
   */
  enhanceButtons(root) {
    const buttons = root.querySelectorAll('button:not([aria-label]), [role="button"]:not([aria-label])');
    
    buttons.forEach((button) => {
      if (this.processedElements.has(button)) return;

      // Try to derive label from content
      const text = button.textContent?.trim();
      const title = button.getAttribute('title');
      const icon = button.querySelector('svg, i, [class*="icon"]');

      if (!button.getAttribute('aria-label')) {
        if (text && text.length > 0 && text.length < 50) {
          button.setAttribute('aria-label', text);
        } else if (title) {
          button.setAttribute('aria-label', title);
        } else if (icon) {
          // Try to get label from icon class or nearby text
          const iconClass = icon.getAttribute('class') || '';
          const labelFromClass = this.extractLabelFromClass(iconClass);
          if (labelFromClass) {
            button.setAttribute('aria-label', labelFromClass);
          }
        }
      }

      // Ensure button has role
      if (!button.getAttribute('role') && button.tagName !== 'BUTTON') {
        button.setAttribute('role', 'button');
      }

      // Add keyboard support for non-button elements
      if (button.tagName !== 'BUTTON' && !button.getAttribute('tabindex')) {
        button.setAttribute('tabindex', '0');
      }

      this.processedElements.add(button);
    });
  }

  /**
   * Enhance input elements
   */
  enhanceInputs(root) {
    const inputs = root.querySelectorAll('input, textarea, select');

    inputs.forEach((input) => {
      if (this.processedElements.has(input)) return;

      const id = input.id;
      const name = input.name;
      const placeholder = input.placeholder;
      const type = input.type;

      // Find associated label
      let label = null;
      if (id) {
        label = document.querySelector(`label[for="${id}"]`);
      }

      // If no label, look for parent label
      if (!label) {
        label = input.closest('label');
      }

      // If still no label, try to find nearby label text
      if (!label && !input.getAttribute('aria-label')) {
        const parent = input.parentElement;
        const labelText = parent?.querySelector('label, .label, [class*="label"]');
        
        if (labelText) {
          const labelId = `a11y-label-${Math.random().toString(36).substr(2, 9)}`;
          labelText.id = labelText.id || labelId;
          input.setAttribute('aria-labelledby', labelText.id);
        } else if (placeholder) {
          input.setAttribute('aria-label', placeholder);
        } else if (name) {
          // Convert name to readable label
          const readableName = name
            .replace(/([A-Z])/g, ' $1')
            .replace(/[_-]/g, ' ')
            .trim();
          input.setAttribute('aria-label', readableName);
        }
      }

      // Add required indicator
      if (input.required && !input.getAttribute('aria-required')) {
        input.setAttribute('aria-required', 'true');
      }

      // Add invalid state
      if (input.validity && !input.validity.valid) {
        input.setAttribute('aria-invalid', 'true');
      }

      this.processedElements.add(input);
    });
  }

  /**
   * Enhance link elements
   */
  enhanceLinks(root) {
    const links = root.querySelectorAll('a:not([aria-label])');

    links.forEach((link) => {
      if (this.processedElements.has(link)) return;

      const text = link.textContent?.trim();
      const title = link.getAttribute('title');
      const href = link.getAttribute('href');

      // Add aria-label if needed
      if (!text || text.length === 0) {
        if (title) {
          link.setAttribute('aria-label', title);
        } else if (href) {
          // Extract meaningful label from href
          const urlLabel = this.extractLabelFromUrl(href);
          if (urlLabel) {
            link.setAttribute('aria-label', urlLabel);
          }
        }
      }

      // Mark external links
      if (href && (href.startsWith('http') || href.startsWith('//'))) {
        if (!href.includes(window.location.hostname)) {
          link.setAttribute('rel', (link.getAttribute('rel') || '') + ' noopener');
          if (!link.getAttribute('aria-label')?.includes('external')) {
            const currentLabel = link.getAttribute('aria-label') || text || 'Link';
            link.setAttribute('aria-label', `${currentLabel} (opens in new tab)`);
          }
        }
      }

      this.processedElements.add(link);
    });
  }

  /**
   * Enhance images
   */
  enhanceImages(root) {
    const images = root.querySelectorAll('img:not([alt])');

    images.forEach((img) => {
      if (this.processedElements.has(img)) return;

      // Try to derive alt from filename or parent context
      const src = img.getAttribute('src') || '';
      const filename = src.split('/').pop()?.split('.')[0] || '';
      const readableAlt = filename
        .replace(/[-_]/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim();

      if (readableAlt && readableAlt.length > 0) {
        img.setAttribute('alt', readableAlt);
      } else {
        // Mark as decorative
        img.setAttribute('alt', '');
        img.setAttribute('role', 'presentation');
      }

      this.processedElements.add(img);
    });
  }

  /**
   * Enhance interactive elements (divs with click handlers, etc.)
   */
  enhanceInteractiveElements(root) {
    // Find clickable divs and spans
    const clickables = root.querySelectorAll('[onclick], [class*="clickable"], [class*="btn"], [class*="button"]');

    clickables.forEach((el) => {
      if (this.processedElements.has(el)) return;
      if (el.tagName === 'BUTTON' || el.tagName === 'A' || el.tagName === 'INPUT') return;

      // Add button role if not present
      if (!el.getAttribute('role')) {
        el.setAttribute('role', 'button');
      }

      // Add tabindex if not present
      if (!el.getAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      // Add keyboard support
      if (!el.dataset.a11yKeyboard) {
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            el.click();
          }
        });
        el.dataset.a11yKeyboard = 'true';
      }

      this.processedElements.add(el);
    });
  }

  /**
   * Enhance navigation elements
   */
  enhanceNavigation(root) {
    // Find nav elements
    const navs = root.querySelectorAll('nav:not([aria-label]), [role="navigation"]:not([aria-label])');

    navs.forEach((nav, index) => {
      if (this.processedElements.has(nav)) return;

      // Add aria-label based on context
      const heading = nav.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading) {
        nav.setAttribute('aria-label', heading.textContent?.trim() || `Navigation ${index + 1}`);
      } else {
        nav.setAttribute('aria-label', `Navigation ${index + 1}`);
      }

      this.processedElements.add(nav);
    });

    // Enhance skip links if not present
    if (!document.querySelector('a[href="#main-content"], .skip-link')) {
      const mainContent = document.querySelector('main, [role="main"], #root');
      if (mainContent && !mainContent.id) {
        mainContent.id = 'main-content';
      }
    }
  }

  /**
   * Enhance tables
   */
  enhanceTables(root) {
    const tables = root.querySelectorAll('table');

    tables.forEach((table) => {
      if (this.processedElements.has(table)) return;

      // Add role if needed
      if (!table.getAttribute('role')) {
        table.setAttribute('role', 'table');
      }

      // Enhance headers
      const headers = table.querySelectorAll('th');
      headers.forEach((th) => {
        if (!th.getAttribute('scope')) {
          // Determine if row or column header
          const parent = th.parentElement;
          const isInThead = th.closest('thead') !== null;
          th.setAttribute('scope', isInThead ? 'col' : 'row');
        }
      });

      this.processedElements.add(table);
    });
  }

  /**
   * Extract a readable label from CSS class names
   */
  extractLabelFromClass(className) {
    // Common icon patterns
    const patterns = [
      /icon[-_]?(\w+)/i,
      /fa[-_](\w+)/i,
      /lucide[-_](\w+)/i,
      /(\w+)[-_]?icon/i
    ];

    for (const pattern of patterns) {
      const match = className.match(pattern);
      if (match && match[1]) {
        return match[1]
          .replace(/([A-Z])/g, ' $1')
          .replace(/[-_]/g, ' ')
          .trim();
      }
    }

    return null;
  }

  /**
   * Extract a readable label from URL
   */
  extractLabelFromUrl(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const lastSegment = pathname.split('/').filter(Boolean).pop();
      if (lastSegment) {
        return lastSegment
          .replace(/[-_]/g, ' ')
          .replace(/\.\w+$/, '')
          .trim();
      }
    } catch (e) {
      // Invalid URL
    }
    return null;
  }

  /**
   * Disconnect the observer
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.initialized = false;
  }
}

// Export singleton instance
export const domEnhancer = new DOMEnhancer();
export default domEnhancer;
